import { z } from 'zod';
import { MONEY_PATTERN } from '@miraki/money';
import { zId } from './primitives.js';

/**
 * Milestone payments — 30 / 30 / 30 / 10 (docs/plans/milestone-payments).
 *
 * Stage satisfaction is DERIVED: stage n is met when the VERIFIED proofs on the order reach
 * `cumulativePct` of `agreedTotal`. Nothing here is a stored flag (invariant I1). An order whose
 * `scheduled` is false is a legacy order and keeps the legacy payment rules unchanged (I3).
 *
 * These are RESPONSE shapes, so decimals are plain strings validated against the money pattern,
 * not `zMoney` (which transforms to a `Money` object on input).
 */
const zDecimalString = z.string().regex(MONEY_PATTERN, 'must be a decimal amount');

export const zMilestoneAction = z.enum(['beginManufacture', 'markReady', 'ship', 'markDelivered']);
export type MilestoneAction = z.infer<typeof zMilestoneAction>;

export const zMilestoneStage = z.object({
  sequence: z.number().int().min(1).max(4),
  /** 'M1' … 'M4' */
  label: z.string(),
  /** This stage's own share, e.g. '30.0000'. */
  pct: zDecimalString,
  /** Running total, e.g. '60.0000'. */
  cumulativePct: zDecimalString,
  /** This stage's own amount. Stage 4 absorbs the rounding remainder. */
  amount: zDecimalString,
  cumulativeAmount: zDecimalString,
  gates: zMilestoneAction,
  state: z.enum(['PAID', 'DUE', 'UPCOMING']),
  /** To reach `cumulativeAmount`; '0.0000' once PAID. */
  shortfall: zDecimalString,
});
export type MilestoneStage = z.infer<typeof zMilestoneStage>;

export const zMilestoneProof = z.object({
  id: zId,
  amount: zDecimalString,
  status: z.enum(['PENDING', 'VERIFIED', 'REJECTED']),
  submittedAt: z.string(),
  verifiedAt: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  /**
   * The consignment invoice this payment is for; null is order-level money.
   *
   * 🔴 D97: `consignmentInvoiceId` is a WIRE field name — it is what the API answers with and what
   * the browser sends. Renaming it here changes the inferred type and turns every sender and
   * receiver red at once, in files owned by four other sessions, so the rename to
   * `consignmentInvoiceId` is **deferred to Wave 3**, which can move the declaration and all of
   * its call sites in one commit. Allow-listed as a transitional entry until then.
   */
  consignmentInvoiceId: zId.nullable(),
});
export type MilestoneProof = z.infer<typeof zMilestoneProof>;

export const zOrderMilestones = z.object({
  orderId: zId,
  currency: z.string().length(3),
  agreedTotal: zDecimalString,
  /** false => legacy order: the UI shows nothing new and every legacy rule applies. */
  scheduled: z.boolean(),
  stages: z.array(zMilestoneStage),
  /** The first stage not yet met; null when all four are met or the order is not scheduled. */
  currentSequence: z.number().int().min(1).max(4).nullable(),
  verified: zDecimalString,
  pending: zDecimalString,
  /** agreedTotal − verified, never negative. */
  remaining: zDecimalString,
  /** Amount still needed to meet the current stage. '0.0000' when none. */
  amountDueNow: zDecimalString,
  /**
   * The most a retailer may submit RIGHT NOW (pending money counted). The screen warns when a
   * typed amount is more than this, or less than `amountDueNow`; the API enforces the same figure.
   */
  payableNow: zDecimalString,
  /** Why nothing more can be paid yet (e.g. M3 waits for the order to be marked ready), or null. */
  holdReason: z.string().nullable(),
  proofs: z.array(zMilestoneProof),
});
export type OrderMilestones = z.infer<typeof zOrderMilestones>;

/**
 * The shipment's courier charge — known only once the shipment is created, so it sits outside the
 * 30/30/30/10 split. It is its own obligation on the consignment invoice: dispatch waits for it, and
 * nothing can release it unpaid. `charge` is null until a shipment exists.
 */
export const zCourierCharge = z.object({
  consignmentInvoiceId: zId,
  currency: z.string().length(3),
  /** false => a legacy invoice: its courier charge is settled with its balance, and no panel is shown. */
  scheduled: z.boolean(),
  /** The order a courier payment is filed under (the invoice's first order, by reference). */
  anchorOrderId: zId,
  shipmentReference: z.string().nullable(),
  charge: zDecimalString.nullable(),
  /** Verified. */
  paid: zDecimalString,
  /** Submitted and awaiting verification. */
  pending: zDecimalString,
  /** charge − paid, never negative; '0.0000' while there is no charge. */
  outstanding: zDecimalString,
  /** The most that may be submitted now: outstanding − pending. */
  payableNow: zDecimalString,
  /** True when dispatch is waiting on this (scheduled invoice, charge set, not fully verified). */
  blocksDispatch: z.boolean(),
});
export type CourierCharge = z.infer<typeof zCourierCharge>;

/**
 * A proof's attribution to a consignment invoice. Admin may set or correct it while the proof is
 * PENDING; a decided proof never changes. `null` clears it back to order-level money.
 */
export const zAttributeProof = z.object({ consignmentInvoiceId: zId.nullable() });
export type AttributeProof = z.infer<typeof zAttributeProof>;

/** Admin records a refund of a cancelled order's full verified amount. */
export const zRecordRefund = z.object({
  reason: z.string().trim().min(3, 'A reason is required to record a refund').max(500),
});
export type RecordRefund = z.infer<typeof zRecordRefund>;

export const zRefundResult = z.object({
  orderId: zId,
  currency: z.string().length(3),
  /** Σ of the reversing ledger rows written by this call. */
  refunded: zDecimalString,
  /** How many PAYMENT_RECEIVED rows were reversed. */
  entries: z.number().int().nonnegative(),
});
export type RefundResult = z.infer<typeof zRefundResult>;
